import { parse, format, differenceInYears, formatDistance, isValid, isSameDay } from 'date-fns'
import { SITE_TIME_ZONE } from './calendarDate'

/** Trailing words on the sticky ticker; shared by the worker (first paint) and the frontend (refresh). */
export const TICKER_SUFFIX = 'Since Last Incident'

const MINUTE = 60_000
const HOUR = 60 * MINUTE
const DAY = 24 * HOUR

interface FormatSinceOptions {
  /** Full CMS timestamp (`ck-dayssince-iso`). Enables the hour-level display. */
  iso?: string | null
  /** Calendar day stamped by the worker as "MMMM dd, yyyy" (`ck-dayssince`). */
  dateString?: string | null
  /** Trailing words: "ago" for list rows, "Since Last Incident" for the ticker. */
  suffix?: string
  now?: Date
}

/**
 * Turns an incident date into relative copy.
 *
 * - under 24 hours old  → "8 hours ago" / "25 minutes ago" (needs a real time of day in the CMS)
 * - dated today, but the time is missing or still ahead of us → "Today"
 * - under a year        → "3 days ago", "5 months ago"
 * - a year or more      → "Apr 20, 2025"
 *
 * Calendar-day maths runs on the site's clock (Eastern) wherever the code happens to execute.
 * The worker (UTC) writes this text into the page and the browser (visitor's zone) re-renders
 * it; on the same clock they agree to the letter, so nothing changes on load.
 *
 * Returns null when there is nothing usable, so callers can leave the Webflow text alone.
 */
export function formatSince({ iso, dateString, suffix = 'ago', now = new Date() }: FormatSinceOptions): string | null {
  const stamp = iso ? new Date(iso) : null
  if (stamp && isValid(stamp)) {
    const elapsed = now.getTime() - stamp.getTime()
    if (elapsed >= 0 && elapsed < DAY) {
      if (elapsed < HOUR) return `${plural(Math.max(1, Math.floor(elapsed / MINUTE)), 'minute')} ${suffix}`
      return `${plural(Math.floor(elapsed / HOUR), 'hour')} ${suffix}`
    }
  }

  const today = onSiteClock(now)
  const day = dateString ? parse(dateString, 'MMMM dd, yyyy', today) : stamp && onSiteClock(stamp)
  if (!day || !isValid(day)) return null

  // Same calendar day but no usable time (date-only entry, or a time later today):
  // hours counted from midnight would be invented precision.
  if (isSameDay(day, today) || day.getTime() > today.getTime()) {
    return suffix === 'ago' ? 'Today' : `Less than a day ${suffix}`
  }

  if (differenceInYears(today, day) >= 1) return format(day, 'MMM d, yyyy')
  const distance = formatDistance(day, today).replace(/about |over |almost /g, '')
  return `${distance} ${suffix}`
}

/** True while the hour-level display applies, i.e. the text will change within the hour. */
export function isRecent(iso: string | null | undefined, now = new Date()): boolean {
  if (!iso) return false
  const elapsed = now.getTime() - new Date(iso).getTime()
  return elapsed >= 0 && elapsed < DAY
}

/** The same instant, re-expressed so its local fields read as the site's wall clock. */
function onSiteClock(date: Date): Date {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: SITE_TIME_ZONE,
    year: 'numeric', month: 'numeric', day: 'numeric',
    hour: 'numeric', minute: 'numeric', second: 'numeric', hourCycle: 'h23',
  }).formatToParts(date)
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value)
  return new Date(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'), get('second'))
}

function plural(count: number, unit: string) {
  return `${count} ${unit}${count === 1 ? '' : 's'}`
}
